import React, { useEffect, useRef } from "react";
import {
  consentWasAlreadyGiven,
  shouldAskForConsent,
} from "@helpers/AnalyticsHelper";

// Add type declarations for Hotjar
declare global {
  interface Window {
    hj: {
      (...args: unknown[]): void;
      q: unknown[];
    };
    _hjSettings: {
      hjid: string;
      hjsv: number;
    };
  }
}

interface ThirdPartyScriptsProps {
  hubSpotPortalId?: string;
  hubSpotRegion?: string;
  hotjarId?: string;
  hotjarVersion?: number;
}

/**
 * Third Party Scripts Component
 *
 * This component loads Hotjar and HubSpot chat widgets only after user consent is given
 * (for GDPR compliance) or if the user is outside GDPR regions.
 *
 * @param hubSpotPortalId - Your HubSpot portal ID (e.g., "25254818")
 * @param hubSpotRegion - HubSpot region (defaults to "eu1" for European region)
 * @param hotjarId - Your Hotjar site ID (e.g., "6451264")
 * @param hotjarVersion - Hotjar script version (defaults to 6)
 */
const ThirdPartyScripts: React.FC<ThirdPartyScriptsProps> = ({
  hubSpotPortalId = "25254818",
  hubSpotRegion = "eu1",
  hotjarId = "6451264",
  hotjarVersion = 6,
}) => {
  const isLoadedRef = useRef(false);

  useEffect(() => {
    // Check if we should load the scripts
    const shouldLoadScripts =
      !shouldAskForConsent() || consentWasAlreadyGiven();

    if (shouldLoadScripts && !isLoadedRef.current) {
      loadHotjar();
      loadHubSpotChat();
      isLoadedRef.current = true;
    }

    // Check for consent changes periodically (since consent is stored in cookies)
    const interval = window.setInterval(() => {
      const currentConsent = consentWasAlreadyGiven();
      if (currentConsent && !isLoadedRef.current) {
        loadHotjar();
        loadHubSpotChat();
        isLoadedRef.current = true;
      }
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [hubSpotPortalId, hubSpotRegion, hotjarId, hotjarVersion]);

  const loadHotjar = () => {
    if (
      typeof window === "undefined" ||
      document.getElementById("hotjar-script")
    ) {
      return;
    }

    // Initialize Hotjar
    window.hj =
      window.hj ||
      function () {
        (window.hj.q = window.hj.q || []).push(arguments);
      };

    window._hjSettings = {
      hjid: hotjarId,
      hjsv: hotjarVersion,
    };

    // Create and load the Hotjar script
    const script = document.createElement("script");
    script.id = "hotjar-script";
    script.async = true;
    script.src = `https://static.hotjar.com/c/hotjar-${hotjarId}.js?sv=${hotjarVersion}`;

    document.head.appendChild(script);
  };

  const loadHubSpotChat = () => {
    if (
      typeof window === "undefined" ||
      document.getElementById("hs-script-loader")
    ) {
      return;
    }

    // Create and load the HubSpot script
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "hs-script-loader";
    script.async = true;
    script.defer = true;
    script.src = `//js-${hubSpotRegion}.hs-scripts.com/${hubSpotPortalId}.js`;

    document.head.appendChild(script);
  };

  // This component doesn't render anything visible
  // The Hotjar and HubSpot widgets will appear as floating elements
  return null;
};

export default ThirdPartyScripts;
